<script setup lang="ts">
import type {CommentData} from "~/types/CommentData";
import {formatSeniority} from "~/utils/format";
import {$api} from "~/composables/useApiFetch";
import ModalConfirm from "~/components/ModalConfirm.vue";
import type {JsonApiResponse} from "~/types/JsonApiResponse";

const props = defineProps<{
  comment: CommentData
  isReply?: boolean
  showContext?: boolean
}>();

const emit = defineEmits(["deleted"]);

const appUser = useAppUser();
const toast = useToast();
const overlay = useOverlay();

const isReporting = ref(false);
const isReported = ref(props.comment.is_reported);
const isDeleting = ref(false);

const revealSpoiler = ref(false);

const showReplyForm = ref(false);
const replyContent = ref("");
const isSubmitting = ref(false);

const isAuthor = computed(() => {
  if (!appUser.value || !props.comment.chapter?.project?.authors) return false;
  return props.comment.chapter.project.authors.some(a => a.uuid === appUser.value?.uuid);
});

const canReply = computed(() => {
  return !props.isReply && isAuthor.value && (!props.comment.replies || props.comment.replies.length === 0);
});

async function reportComment() {
  if (isReporting.value || isReported.value) return;

  isReporting.value = true;
  try {
    await $api(`/api/comment/${props.comment.id}/report`, {
      method: "POST",
    });
    isReported.value = true;
    toast.add({
      title: "Commentaire signalé",
      description: "Merci de nous avoir aidé à maintenir la communauté saine.",
      color: "success",
    });
  } catch (e) {
    console.error("Erreur lors du signalement du commentaire", e);
    toast.add({
      title: "Erreur",
      description: "Une erreur est survenue lors du signalement du commentaire.",
      color: "error",
    });
  } finally {
    isReporting.value = false;
  }
}

function confirmDelete() {
  const modal = overlay.create(ModalConfirm, {
    props: {
      title: "Supprimer le commentaire",
      description: "Êtes-vous sûr de vouloir supprimer votre commentaire ? Cette action est définitive.",
      confirmLabel: "Supprimer",
      confirmColor: "error",
      onConfirm: async () => {
        modal.close();
        await deleteComment();
      },
      onCancel: () => {
        modal.close();
      },
    },
  });
  modal.open();
}

async function deleteComment() {
  if (isDeleting.value) return;

  isDeleting.value = true;
  try {
    await $api(`/api/comment/${props.comment.id}`, {
      method: "DELETE",
    });
    toast.add({
      title: "Commentaire supprimé",
      description: "Votre commentaire a bien été supprimé.",
      color: "success",
    });
    emit("deleted", props.comment.id);
  } catch (e) {
    console.error("Erreur lors de la suppression du commentaire", e);
    toast.add({
      title: "Erreur",
      description: "Une erreur est survenue lors de la suppression du commentaire.",
      color: "error",
    });
  } finally {
    isDeleting.value = false;
  }
}

async function submitReply() {
  if (!replyContent.value || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    const res = await $api<JsonApiResponse<CommentData>>(`/api/chapter/${props.comment.chapter?.uuid}/comment`, {
      method: "POST",
      body: {
        content: replyContent.value,
        parent_id: props.comment.id,
      },
    });

    if (!props.comment.replies) props.comment.replies = [];
    props.comment.replies.push(res.data);

    replyContent.value = "";
    showReplyForm.value = false;
    toast.add({
      title: "Réponse envoyée",
      description: "Votre réponse a bien été ajoutée.",
      color: "success",
    });
  } catch (e) {
    console.error("Erreur lors de l'envoi de la réponse", e);
    toast.add({
      title: "Erreur",
      description: "Une erreur est survenue lors de l'envoi de la réponse.",
      color: "error",
    });
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div :class="!isReply ? 'border-b border-gray-100 mb-6 pb-6':''">
    <div v-if="!isReply && showContext && comment.chapter" class="mb-6">
      <div class="flex items-center gap-1">
        <ULink :to="`/project/${comment.chapter.project?.uuid}`" class="hover:underline text-base font-semibold !text-primary">
          {{ comment.chapter.project?.title }}
        </ULink>
      </div>
    </div>
    <div class="flex items-center justify-between gap-2">
      <div class="mb-2">
        <NuxtLink :to="isReply ? `/author/${comment.user.uuid}` : `/reader/${comment.user.uuid}`" class="flex items-center gap-2">
          <UAvatar
              :src="comment.user.profiles?.reader?.picture || comment.user.profiles?.author?.picture"
              :alt="comment.user.name"
              size="xl"
          />
          <div>
            <div class="flex flex-col md:flex-row">
              <span class="font-bold">{{ comment.user.name }}</span>
            </div>
            <div class="flex items-center gap-1 text-sm text-gray-300" v-if="comment.chapter">
              <span>Chapitre {{ comment.chapter.order }}</span> • <span>il y a {{ formatSeniority(comment.created_at) }}</span>
            </div>
          </div>
        </NuxtLink>
      </div>

      <div>
        <Tooltip :text="isReported ? 'Commentaire déjà signalé' : 'Signaler le commentaire'">
          <UButton
              v-if="appUser"
              :icon="isReported ? 'i-heroicons-flag-solid':'i-heroicons-flag'"
              variant="ghost"
              size="xs"
              :loading="isReporting"
              :disabled="isReported"
              @click="reportComment"
          />
        </Tooltip>
        <Tooltip text="Supprimer le commentaire">
          <UButton
              v-if="appUser && comment.user.uuid===appUser.uuid"
              icon="i-heroicons-trash"
              variant="ghost"
              size="xs"
              :loading="isDeleting"
              @click="confirmDelete"
          />
        </Tooltip>
      </div>
    </div>

    <div class="ml-11">
      <UAlert
        v-if="comment.is_spoiler && !revealSpoiler && !isAuthor"
        title="Ce commentaire contient des spoilers."
        icon="i-custom-warning"
        :actions="[{ label: 'Afficher', variant: 'solid', color: 'secondary', size: 'sm', onClick: () => { revealSpoiler = true } }]"
        color="primary"
        variant="solid"
        class="mb-2 !bg-gradient-explicit text-inverted"
      />
      <UBadge v-if="comment.is_spoiler && revealSpoiler" class="bg-gradient-explicit text-white!" icon="i-custom-warning" size="xs">Spoiler</UBadge>
      <div :class="comment.is_spoiler && !revealSpoiler && !isAuthor ? 'blur-sm select-none pointer-events-none' : ''">
        {{ comment.content }}
      </div>
      <div v-if="comment.emotions?.length" class="flex gap-x-2 mt-4">
        <div v-for="emotion in comment.emotions" :key="emotion.id"><Tooltip :text="emotion.title"><UIcon :name="emotion.icon" class="size-5"/></Tooltip></div>
      </div>
    </div>

    <div v-if="comment.replies && comment.replies.length > 0" class="flex flex-col gap-2 mt-6">
      <ProjectComment
          v-for="reply in comment.replies"
          :key="reply.id"
          :comment="reply"
          is-reply
          :show-context="showContext"
          @deleted="(id) => { if (comment.replies) comment.replies = comment.replies.filter(r => r.id !== id) }"
          class="ml-11"
      />
    </div>
    <div v-if="canReply"  class="ml-11 mt-6">
      <div v-if="!showReplyForm">
        <UButton
            size="sm"
            label="Répondre"
            @click="showReplyForm = true"
        />
      </div>
      <div v-else class="flex flex-col gap-2">
        <UTextarea
            v-model="replyContent"
            placeholder="Votre réponse..."
            autofocus
        />
        <div class="flex justify-end gap-2">
          <UButton
              label="Annuler"
              size="sm"
              @click="showReplyForm = false"
          />
          <UButton
              label="Répondre"
              color="primary"
              variant="solid"
              size="sm"
              :loading="isSubmitting"
              :disabled="!replyContent"
              @click="submitReply"
          />
        </div>
      </div>
    </div>
  </div>
</template>
